import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import nintendoSearchService from "../service/nintendoSearch.service";
import { siteImage } from "../service/pspricesDeals.service";
import NintendoPickedGame, { withLivePrice } from "../components/nintendoDeals/NintendoPickedGame";
import { CARD_REGION, useNintendoCards, useStoreCards } from "../components/nintendoDeals/useNintendoCards";
import PsPickedGame from "../components/psDeals/PsPickedGame";
import { PS_CARD_REGIONS, psCatalogs, usePsCards } from "../components/psDeals/psCards";
import SteamPickedGame from "../components/steamDeals/SteamPickedGame";
import {
  STEAM_STORE_ID,
  TOPUP_REGIONS,
  USD_CARDS_REGION,
  useSteamPricing,
} from "../components/steamDeals/steamTopup";
import SteamTopUpWidget from "../components/steam/SteamTopUpWidget";
import { rubAtCardRate } from "../utils/pickCards";
import { REGION_LABELS } from "./favoriteKey";

const hasStock = (catalog) => Boolean(catalog?.denominations.some((item) => item.stock > 0));

function NoCards({ to, label }) {
  return (
    <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
      Карт этого региона сейчас нет.{" "}
      <Link to={to} className="font-medium underline">
        {label}
      </Link>
    </div>
  );
}

function Loading() {
  return <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Загружаем карты…</p>;
}

/**
 * Карты Nintendo под игру из избранного: тот же подбор и оформление, что на витрине.
 * offer — цена в выбранном регионе ({ price, original, percent, endsAt, currency, nsuid }).
 */
export function FavoriteBuyNintendo({ item, region, offer }) {
  const cards = useNintendoCards();
  const cardRegion = CARD_REGION[region];
  const catalog = cardRegion ? cards.cardsFor(cardRegion) : null;
  const label = REGION_LABELS.nintendo[region] || region;
  const nsuid = offer?.nsuid || (item.region === region ? item.nsuid : null);
  const [balance, setBalance] = useState("");
  const [live, setLive] = useState(undefined);

  useEffect(() => {
    setLive(undefined);
    if (!nsuid || !region) return undefined;
    const controller = new AbortController();
    nintendoSearchService
      .livePrice(region, nsuid, controller.signal)
      .then(setLive)
      .catch((error) => {
        if (error.name !== "AbortError") setLive(null);
      });
    return () => controller.abort();
  }, [nsuid, region]);

  const discountsLink = { to: `/nintendo-discount?region=${region}`, label: `Скидки Nintendo ${label}` };
  if (!cardRegion) return <NoCards {...discountsLink} />;
  if (!cards.loaded && !cards.error) return <Loading />;
  if (cards.loaded && !hasStock(catalog)) return <NoCards {...discountsLink} />;

  const currency = catalog?.currency || offer?.currency;
  const usable = offer && offer.currency === currency;
  const base = {
    game: {
      nsuid,
      title: item.title,
      image: item.image,
      seoPath: item.seoPath,
      price: usable ? offer.price : null,
      original: usable ? offer.original ?? null : null,
      percent: usable ? offer.percent ?? null : null,
      endsAt: usable ? offer.endsAt ?? null : null,
      rub: usable ? rubAtCardRate(offer.price, catalog?.denominations) : null,
    },
    context: { region, currency, label, cardRegion, cards: catalog },
  };
  const picked = live === undefined ? base : withLivePrice(base, live);

  return (
    <NintendoPickedGame
      key={`${item.gameKey}-${region}`}
      game={picked.game}
      context={picked.context}
      live={picked.live}
      catalog={catalog}
      initialOpen
      onRefreshCards={cards.refresh}
      balance={balance}
      onBalance={setBalance}
    />
  );
}

/** Карты PSN под игру; stale — цена не с витрины, а сохранённая при добавлении. */
export function FavoriteBuyPs({ item, current, staleNote }) {
  const cards = usePsCards();
  const config = PS_CARD_REGIONS[item.region];
  const catalogs = useMemo(
    () => psCatalogs(item.region, cards.cardsFor),
    [item.region, cards.cardsFor],
  );
  const label = REGION_LABELS.playstation[item.region] || item.region;
  const discountsLink = { to: "/playstation-discount", label: "Скидки PlayStation" };

  if (!config) return <NoCards {...discountsLink} />;
  if (cards.loaded && !catalogs.some(hasStock)) return <NoCards {...discountsLink} />;
  if (!(current?.price > 0) || current.currency !== config.currency) {
    return (
      <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
        Цена игры неизвестна — откройте её на{" "}
        <Link to="/playstation-discount" className="underline">
          странице скидок
        </Link>
        .
      </p>
    );
  }

  return (
    <PsPickedGame
      pick={{
        game: {
          id: item.gameKey,
          title: item.title,
          image: item.image ? siteImage(item.image) : null,
          price: current.price,
          original: current.original ?? null,
          percent: current.percent ?? null,
          endsAt: current.endsAt ?? null,
          storeUrl: item.url,
        },
        region: item.region,
        currency: config.currency,
        label,
      }}
      catalogs={catalogs}
      cardsError={cards.error}
      onRefreshCards={cards.refresh}
      initialOpen
      priceNote={staleNote}
    />
  );
}

/** Пополнение Steam под игру; без цены — обычное пополнение без суммы под игру. */
export function FavoriteBuySteam({ item, current }) {
  const region = item.region || "ru";
  const pricing = useSteamPricing();
  const cards = useStoreCards(STEAM_STORE_ID);
  const label = REGION_LABELS.steam[region] || region;

  if (!(current?.price > 0)) {
    return TOPUP_REGIONS.has(region) ? (
      <div className="mt-3">
        <SteamTopUpWidget />
      </div>
    ) : (
      <Link
        to={`/store/steam?region=${USD_CARDS_REGION}`}
        className="mt-3 block w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-center text-sm font-semibold text-white transition hover:bg-indigo-500"
      >
        Купить карты Steam США
      </Link>
    );
  }

  return (
    <SteamPickedGame
      key={`${item.gameKey}-${region}`}
      game={{
        appId: item.appId,
        title: item.title,
        cover: siteImage(item.image),
        url: item.url,
        currency: current.currency,
        price: current.price,
        original: current.original ?? null,
        percent: current.percent ?? null,
      }}
      region={region}
      label={label}
      pricing={pricing}
      usdCards={cards.cardsFor(USD_CARDS_REGION)}
      cardsSettled={cards.loaded || cards.error}
      onRefreshCards={cards.refresh}
      initialOpen
    />
  );
}
